import React, { Component, type ReactElement } from 'react';
import InlineMessage from '@atlaskit/inline-message';

export type Props = {
	numUsed: number;
	limit: number;
	warningThreshold: number;
	renderNearLimitMessage: (limit: number) => string;
	renderNearLimitDescription: (limit: number) => ReactElement;
	renderAtLimitMessage: (limit: number) => string;
	renderAtLimitDescription: (limit: number) => ReactElement;
	renderOverLimitMessage: (limit: number) => string;
	renderOverLimitDescription: (limit: number) => ReactElement;
	onMessageRendered: () => void;
};

export const DEFAULT_SHOW_WARNING_THRESHOLD = 5;

export const BELOW_LIMIT = 'BELOW_LIMIT' as const;
export const NEAR_LIMIT = 'NEAR_LIMIT' as const;
export const AT_LIMIT = 'AT_LIMIT' as const;
export const OVER_LIMIT = 'OVER_LIMIT' as const;
export type LimitStatus =
	| typeof BELOW_LIMIT
	| typeof NEAR_LIMIT
	| typeof AT_LIMIT
	| typeof OVER_LIMIT;

export const messageTypesByLimitStatus = {
	[BELOW_LIMIT]: 'info',
	[NEAR_LIMIT]: 'info',
	[AT_LIMIT]: 'warning',
	[OVER_LIMIT]: 'warning',
} as const;

// eslint-disable-next-line jira/react/no-class-components
export default class LimitMessage extends Component<Props> {
	static defaultProps = {
		warningThreshold: DEFAULT_SHOW_WARNING_THRESHOLD,
	};

	componentDidMount() {
		const limitStatus: LimitStatus = this.getLimitStatus();
		const { onMessageRendered } = this.props;
		if (limitStatus !== BELOW_LIMIT) {
			onMessageRendered && onMessageRendered();
		}
	}

	getLimitStatus() {
		const { numUsed, limit, warningThreshold } = this.props;
		const remaining = limit - numUsed;

		if (remaining === 0) {
			return AT_LIMIT;
		}
		if (remaining < 0) {
			return OVER_LIMIT;
		}
		return remaining <= warningThreshold ? NEAR_LIMIT : BELOW_LIMIT;
	}

	getMessageAndDescription(limitStatus: LimitStatus) {
		const { limit } = this.props;
		if (limitStatus === NEAR_LIMIT) {
			return [
				this.props.renderNearLimitMessage(limit),
				this.props.renderNearLimitDescription(limit),
			];
		}
		if (limitStatus === AT_LIMIT) {
			return [this.props.renderAtLimitMessage(limit), this.props.renderAtLimitDescription(limit)];
		}
		return [
			this.props.renderOverLimitMessage(limit),
			this.props.renderOverLimitDescription(limit),
		] as const;
	}

	render() {
		const limitStatus: LimitStatus = this.getLimitStatus();
		if (limitStatus === BELOW_LIMIT) {
			return null;
		}
		const messageType = messageTypesByLimitStatus[limitStatus];
		const [message, description] = this.getMessageAndDescription(limitStatus);

		return (
			<InlineMessage appearance={messageType} title={message}>
				{description}
			</InlineMessage>
		);
	}
}
